import { usePublishPageCrumb } from '../../store/usePageSettingsStore';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AlertCircle, ArrowLeft, Ban, Check, Loader2, RotateCcw, Send, Stamp, Upload } from 'lucide-react';
import { useAuthStore } from '../../store/useAuthStore';
import {
  signatureKeys,
  useCancelCircuit,
  useCircuit,
  useDispatchCircuit,
  useMyParapheUrl,
  useMySignatureUrl,
  useRejectStep,
  useResubmitCircuit,
  useSetParapheStamps,
  useSetParapheZones,
  useSignStep,
} from '../../hooks/queries/useSignatureCircuits';
import { getDownloadUrl, replaceFileContents } from '../../services/files/filesService';
import { setParapheZones } from '../../services/signature/signatureService';
import { circuitPreviewKind } from '../../services/signature/parapheurRoutes';
import {
  CIRCUIT_DRAFT,
  canDispatchVisaRequest,
  canResendCorrected,
  canResubmitRevision,
  canWithdrawVisaRequest,
  parseDraftIdFromSourceRef,
} from '../../services/signature/circuitRevision';
import { CIRCUIT_KIND_VALIDATION, parseCircuitKind } from '../../lib/signature/circuitKind';
import { PARAPHEUR_COPY, fillAndSignHint } from '../../services/signature/parapheurCopy';
import {
  findDraftByFinalizedFile,
  reopenDraft,
  type DocumentDraft,
} from '../../services/documents/documentService';
import { GenerateDocumentModal } from '../documents/GenerateDocumentModal';
import {
  allBoxesFilled,
  boxStamp,
  initialsFromName,
  moveZone,
  parseFilledIds,
  parseParapheZones,
  placeZoneAt,
  setZoneKind,
  unfillOne,
  type ParapheZone,
  type ZoneKind,
} from '../../services/signature/parapheBoxes';
import {
  addHighlight,
  addNote,
  hasRefuseMarks,
  moveAnnotation,
  parseRefuseAnnotations,
  removeAnnotation,
  setAnnotationText,
  type RefuseAnnotation,
} from '../../services/signature/refuseAnnotations';
import { Button } from '../ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { CircuitStatusBadge } from './circuitStatusBadge';
import { CircuitTimeline } from './CircuitTimeline';
import { CollaboratorLink } from '../collaborators/CollaboratorLink';
import { RejectCommentDialog } from './RejectCommentDialog';
import { VisaReauthDialog } from './VisaReauthDialog';
import { PdfDocumentViewer } from './PdfDocumentViewer';
import { ParapheZoneMarks, type ZoneFill } from './ParapheZoneMarks';
import { RefuseAnnotationMarks } from './RefuseAnnotationMarks';
import { RefuseMarksPanel } from './RefuseMarksPanel';

export function CircuitReviewPage() {
  const { circuitId } = useParams<{ circuitId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: circuit, isLoading, error: loadError } = useCircuit(circuitId);
  usePublishPageCrumb(circuit?.title);
  const collab = useAuthStore((s) => s.collaborator);
  const { data: myParaphe } = useMyParapheUrl();
  const { data: mySignature } = useMySignatureUrl();
  const sign = useSignStep();
  const reject = useRejectStep();
  const cancel = useCancelCircuit();
  const dispatch = useDispatchCircuit();
  const resubmit = useResubmitCircuit();
  const saveZones = useSetParapheZones();
  const saveStamps = useSetParapheStamps();
  const [showReject, setShowReject] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfUrlError, setPdfUrlError] = useState<string | null>(null);
  const [zones, setZones] = useState<ParapheZone[]>([]);
  const [filledIds, setFilledIds] = useState<string[]>([]);
  const [refuseMode, setRefuseMode] = useState(false);
  const [refuseTool, setRefuseTool] = useState<'highlight' | 'note'>('highlight');
  const [draftAnnotations, setDraftAnnotations] = useState<RefuseAnnotation[]>([]);
  const [selectedAnnotationId, setSelectedAnnotationId] = useState<string | null>(null);
  const [highlightDialogId, setHighlightDialogId] = useState<string | null>(null);
  const [highlightDraftText, setHighlightDraftText] = useState('');
  const [showMarks, setShowMarks] = useState(true);
  const [resumeDraft, setResumeDraft] = useState<DocumentDraft | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [showVisaAuth, setShowVisaAuth] = useState(false);
  const [placeKind, setPlaceKind] = useState<ZoneKind>('paraphe');
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);
  const replaceInputRef = useRef<HTMLInputElement>(null);

  const previewKind = circuitPreviewKind(circuit?.file);
  const circuitRecord = circuit as (typeof circuit & {
    paraphe_zones?: unknown;
    document_version?: number;
    revision_baseline_storage_path?: string | null;
  }) | undefined;
  const documentVersion = circuitRecord?.document_version ?? 1;
  const fileStoragePath = circuit?.file?.storage_path ?? null;
  const signedStoragePath = circuit?.file?.signed_storage_path ?? null;
  const isValidation = parseCircuitKind(circuit?.kind) === CIRCUIT_KIND_VALIDATION;
  const showBakedPdf = !isValidation && circuit?.status === 'completed' && Boolean(signedStoragePath);

  useEffect(() => {
    if (!circuit?.file?.id || previewKind !== 'pdf') {
      setPdfUrl(null);
      setPdfUrlError(null);
      return;
    }
    let alive = true;
    setPdfUrl(null);
    setPdfUrlError(null);
    getDownloadUrl(circuit.file.id, { preferSigned: showBakedPdf })
      .then((u) => { if (alive) setPdfUrl(`${u}${u.includes('?') ? '&' : '?'}v=${documentVersion}`); })
      .catch((e: unknown) => {
        if (alive) setPdfUrlError(e instanceof Error ? e.message : 'Impossible d’ouvrir le fichier');
      });
    return () => { alive = false; };
  }, [circuit?.file?.id, previewKind, documentVersion, fileStoragePath, signedStoragePath, showBakedPdf]);

  useEffect(() => {
    if (!circuitRecord) return;
    setZones(parseParapheZones(circuitRecord.paraphe_zones));
  }, [circuitRecord, circuitRecord?.id, circuitRecord?.paraphe_zones]);

  const myStep = circuit?.steps.find((s) => s.collaborator_id === collab?.id && s.status === 'pending') ?? null;
  const isCurrentStepMine = Boolean(myStep && circuit?.steps
    .filter((s) => s.step_order < myStep.step_order)
    .every((s) => s.status === 'signed'));

  useEffect(() => {
    const raw = myStep as { paraphed_zone_ids?: unknown } | null;
    setFilledIds(parseFilledIds(raw?.paraphed_zone_ids));
  }, [myStep]);

  const signedCount = circuit?.steps.filter((s) => s.status === 'signed').length ?? 0;
  const canPlace = Boolean(
    circuit
    && !isValidation
    && (circuit.initiator_id === collab?.id || collab?.role === 'admin')
    && (
      circuit.status === CIRCUIT_DRAFT
      || (circuit.status === 'in_progress' && signedCount === 0)
      || circuit.status === 'awaiting_revision'
    ),
  );
  const canStamp = Boolean(isCurrentStepMine && circuit?.status === 'in_progress');
  const waitingStep = [...(circuit?.steps ?? [])]
    .sort((a, b) => a.step_order - b.step_order)
    .find((s) => s.status === 'pending') ?? null;
  const waitingName = waitingStep?.collaborator?.full_name ?? PARAPHEUR_COPY.fallbackSigner;
  const canRevise = Boolean(circuit && canResubmitRevision({
    status: circuit.status,
    isInitiator: circuit.initiator_id === collab?.id,
    isAdmin: collab?.role === 'admin',
  }));
  const canCancel = Boolean(circuit && canWithdrawVisaRequest({
    status: circuit.status,
    isInitiator: circuit.initiator_id === collab?.id,
    isAdmin: collab?.role === 'admin',
  }));
  const canResend = Boolean(circuit && canResendCorrected({
    status: circuit.status,
    isInitiator: circuit.initiator_id === collab?.id,
    isAdmin: collab?.role === 'admin',
    baselineStoragePath: circuitRecord?.revision_baseline_storage_path,
    currentStoragePath: fileStoragePath,
    boxCount: zones.length,
    kind: parseCircuitKind(circuit.kind),
  }));
  const canDispatch = Boolean(circuit && canDispatchVisaRequest({
    status: circuit.status,
    isInitiator: circuit.initiator_id === collab?.id,
    isAdmin: collab?.role === 'admin',
    boxCount: zones.length,
  }));
  const hasCimeDraft = Boolean(parseDraftIdFromSourceRef(circuit?.file?.source_ref));
  const boxesReady = isValidation || allBoxesFilled(zones.map((z) => z.id), filledIds);
  const savedAnnotations = parseRefuseAnnotations(
    (circuit as { refuse_annotations?: unknown } | undefined)?.refuse_annotations,
  );
  const visibleAnnotations = refuseMode ? draftAnnotations : savedAnnotations;
  const overlayAnnotations = showMarks ? visibleAnnotations : [];

  const filledBy = useMemo(() => {
    const map = new Map<string, ZoneFill>();
    if (!circuit) return map;
    for (const step of circuit.steps) {
      if (step.status !== 'signed') continue;
      const ids = parseFilledIds((step as { paraphed_zone_ids?: unknown }).paraphed_zone_ids);
      const label = initialsFromName(step.collaborator?.full_name ?? '');
      for (const id of ids) {
        map.set(id, { label, mine: false });
      }
    }
    for (const id of filledIds) {
      const kind = zones.find((z) => z.id === id)?.kind ?? 'paraphe';
      const mine = boxStamp(kind, collab?.full_name ?? '', myParaphe, mySignature);
      map.set(id, { label: mine.label, mine: true, imageUrl: mine.imageUrl });
    }
    return map;
  }, [circuit, filledIds, collab?.full_name, myParaphe, mySignature, zones]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-5 h-5 animate-spin text-cime-text-muted" />
      </div>
    );
  }

  if (!circuit || loadError) {
    return (
      <div className="main-container-gdm py-12 text-center space-y-3">
        <p className="text-sm text-cime-text-muted">Circuit introuvable ou accès refusé.</p>
        <Link to="/parapheur" className="text-sm text-cime-primary hover:underline">
          Retour au parapheur
        </Link>
      </div>
    );
  }

  function persistZones(next: ParapheZone[]) {
    setZones(next);
    if (!circuitId) return;
    saveZones.mutate(
      { circuitId, zones: next },
      { onError: (err) => setError(err instanceof Error ? err.message : 'Erreur') },
    );
  }

  function persistStamps(next: string[]) {
    setFilledIds(next);
    if (!myStep) return;
    saveStamps.mutate(
      { stepId: myStep.id, zoneIds: next },
      { onError: (err) => setError(err instanceof Error ? err.message : 'Erreur') },
    );
  }

  function handlePageClick(page: number, x: number, y: number) {
    if (refuseMode && refuseTool === 'note') {
      const id = crypto.randomUUID();
      setDraftAnnotations(addNote(draftAnnotations, page, x, y, '', id));
      setSelectedAnnotationId(id);
      setShowMarks(true);
      setError(null);
      return;
    }
    if (!canPlace || refuseMode) return;
    const id = crypto.randomUUID();
    persistZones([...zones, placeZoneAt(page, x, y, id, placeKind)]);
    setSelectedZoneId(id);
  }

  function handlePageRect(page: number, x0: number, y0: number, x1: number, y1: number) {
    if (!refuseMode || refuseTool !== 'highlight') return;
    const id = crypto.randomUUID();
    setDraftAnnotations(addHighlight(draftAnnotations, page, x0, y0, x1, y1, id));
    setSelectedAnnotationId(id);
    setHighlightDraftText('');
    setHighlightDialogId(id);
    setShowMarks(true);
  }

  function handleMoveAnnotation(id: string, x: number, y: number) {
    setDraftAnnotations((list) => list.map((a) => (a.id === id ? moveAnnotation(a, x, y) : a)));
  }

  function confirmHighlightText() {
    if (!highlightDialogId) return;
    setDraftAnnotations(setAnnotationText(draftAnnotations, highlightDialogId, highlightDraftText));
    setHighlightDialogId(null);
  }

  function handleDeleteZone(id: string) {
    persistZones(zones.filter((z) => z.id !== id));
    if (filledIds.includes(id)) persistStamps(unfillOne(filledIds, id));
    if (selectedZoneId === id) setSelectedZoneId(null);
  }

  function handleMoveZone(id: string, x: number, y: number) {
    persistZones(zones.map((z) => (z.id === id ? moveZone(z, x, y) : z)));
  }

  function handleChangeKind(id: string, kind: ZoneKind) {
    persistZones(zones.map((z) => (z.id === id ? setZoneKind(z, kind) : z)));
  }

  function handleToggleFill(id: string) {
    if (!canStamp) return;
    if (filledIds.includes(id)) persistStamps(unfillOne(filledIds, id));
    else persistStamps([...filledIds, id]);
  }

  function handleParaferTout() {
    persistStamps(zones.map((z) => z.id));
  }

  function handleSign() {
    if (!myStep) return;
    if (!isValidation && !boxesReady) {
      setError(zones.length === 0
        ? PARAPHEUR_COPY.needBoxes
        : PARAPHEUR_COPY.fillRemaining);
      return;
    }
    setError(null);
    setShowVisaAuth(true);
  }

  function handleConfirmVisa(password: string) {
    if (!myStep) return;
    sign.mutate(
      { stepId: myStep.id, password },
      {
        onSuccess: () => setShowVisaAuth(false),
        onError: (err) => setError(err instanceof Error ? err.message : 'Erreur'),
      },
    );
  }

  function handleReject(comment: string) {
    if (!myStep) return;
    if (!hasRefuseMarks(draftAnnotations)) {
      setError('Marquez le PDF avant de renvoyer.');
      return;
    }
    reject.mutate(
      { stepId: myStep.id, comment, annotations: draftAnnotations },
      {
        onSuccess: () => {
          setShowReject(false);
          setRefuseMode(false);
        },
        onError: (err) => setError(err instanceof Error ? err.message : 'Erreur'),
      },
    );
  }

  const circuitTitle = circuit.title;
  const circuitPk = circuit.id;
  const circuitFileId = circuit.file?.id;

  function handleConfirmCancel() {
    cancel.mutate(circuitPk, {
      onSuccess: () => {
        setCancelDialogOpen(false);
        navigate('/parapheur');
      },
      onError: (err) => setError(err instanceof Error ? err.message : 'Erreur'),
    });
  }

  function handleResubmit() {
    resubmit.mutate(circuitPk, {
      onError: (err) => setError(err instanceof Error ? err.message : 'Erreur'),
    });
  }

  function handleDispatch() {
    dispatch.mutate(circuitPk, {
      onError: (err) => setError(err instanceof Error ? err.message : 'Erreur'),
    });
  }

  async function handleReplacePdf(file: File) {
    if (!circuitFileId) return;
    if (file.type !== 'application/pdf') {
      setError('Le remplacement doit être un PDF.');
      return;
    }
    try {
      await replaceFileContents(circuitFileId, file);
      if (circuitId) await setParapheZones(circuitId, []);
      setZones([]);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    } finally {
      await qc.invalidateQueries({ queryKey: signatureKeys.all });
      await qc.invalidateQueries({ queryKey: ['dossier-files'] });
    }
  }

  async function handleReopenDraft() {
    if (!circuitFileId) return;
    try {
      const draft = await findDraftByFinalizedFile(circuitFileId);
      if (!draft) {
        setError('Pas de brouillon CIME lié — remplacez le PDF.');
        return;
      }
      await reopenDraft(draft.id);
      setResumeDraft({ ...draft, status: 'draft' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <header className="flex-shrink-0 border-b border-neutral-200 bg-white px-4 py-2 flex items-center gap-3">
        <Link
          to="/parapheur"
          className="inline-flex items-center gap-1.5 text-sm text-cime-text-muted hover:text-cime-text-primary"
        >
          <ArrowLeft className="w-4 h-4" /> Parapheur
        </Link>
        <h1
          className="min-w-0 truncate text-[17px] font-semibold text-cime-text-primary"
          style={{ fontFamily: 'var(--cime-font-heading)' }}
        >
          {circuit.title}
        </h1>
        <CircuitStatusBadge status={circuit.status as 'draft' | 'in_progress' | 'awaiting_revision' | 'completed' | 'rejected' | 'cancelled'} kind={circuit.kind} />
        {documentVersion > 1 && (
          <span className="text-xs text-cime-text-muted">v{documentVersion}</span>
        )}
      </header>

      <div className="flex-1 min-h-0 flex">
        <div className="flex-1 min-w-[16rem] overflow-auto bg-neutral-200 p-4">
          {previewKind === 'missing' && (
            <p className="text-sm text-cime-text-muted text-center py-16">Document introuvable.</p>
          )}
          {previewKind === 'unsupported' && (
            <p className="text-sm text-cime-text-muted text-center py-16">
              Aperçu disponible uniquement pour les PDF.
            </p>
          )}
          {previewKind === 'pdf' && pdfUrlError && (
            <p className="text-sm text-red-600 text-center py-16">{pdfUrlError}</p>
          )}
          {previewKind === 'pdf' && !pdfUrl && !pdfUrlError && (
            <p className="text-sm text-cime-text-muted text-center py-16">Chargement…</p>
          )}
          {previewKind === 'pdf' && pdfUrl && (
            <>
              {canPlace && !refuseMode && (
                <div className="flex gap-1 mb-3">
                  {(['paraphe', 'signature'] as const).map((kind) => (
                    <button
                      key={kind}
                      type="button"
                      className={`text-xs px-2.5 py-1 rounded-md border ${
                        placeKind === kind
                          ? 'bg-cime-primary text-white border-cime-primary'
                          : 'bg-white text-neutral-700 border-neutral-300'
                      }`}
                      onClick={() => setPlaceKind(kind)}
                    >
                      {kind === 'paraphe' ? PARAPHEUR_COPY.toolParaphe : PARAPHEUR_COPY.toolSignature}
                    </button>
                  ))}
                </div>
              )}
              <PdfDocumentViewer
                url={pdfUrl}
                placeMode={canPlace && !refuseMode}
                onPageClick={
                  (refuseMode && refuseTool === 'note') || (canPlace && !refuseMode)
                    ? handlePageClick
                    : undefined
                }
                onPageRect={refuseMode && refuseTool === 'highlight' ? handlePageRect : undefined}
                overlay={(page) => showBakedPdf ? null : (
                <>
                  <RefuseAnnotationMarks
                    annotations={overlayAnnotations}
                    page={page}
                    canEdit={refuseMode}
                    selectedId={selectedAnnotationId}
                    onSelect={setSelectedAnnotationId}
                    onDelete={(id) => {
                      setDraftAnnotations(removeAnnotation(draftAnnotations, id));
                      if (selectedAnnotationId === id) setSelectedAnnotationId(null);
                    }}
                    onMove={handleMoveAnnotation}
                  />
                  <ParapheZoneMarks
                    zones={zones}
                    page={page}
                    filledBy={filledBy}
                    canPlace={canPlace && !refuseMode}
                    canStamp={canStamp && !refuseMode}
                    selectedId={selectedZoneId}
                    onSelect={setSelectedZoneId}
                    onDelete={handleDeleteZone}
                    onToggleFill={handleToggleFill}
                    onMove={handleMoveZone}
                    onChangeKind={handleChangeKind}
                  />
                </>
              )}
            />
            </>
          )}
        </div>

        {/* Messaging FAB is fixed bottom-4 right-4; keep footer CTAs above it. */}
        <aside className="w-80 shrink-0 border-l border-neutral-200 bg-white flex flex-col pb-20">
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
            {canRevise && (
              <div className="text-xs text-amber-950 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 space-y-2">
                <p>
                  {PARAPHEUR_COPY.reviserBanner}
                </p>
                <div className="flex flex-wrap gap-1">
                  <button
                    type="button"
                    onClick={() => replaceInputRef.current?.click()}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-amber-600 bg-white"
                  >
                    <Upload className="w-3 h-3" /> Remplacer le PDF
                  </button>
                  {hasCimeDraft && (
                    <button
                      type="button"
                      onClick={() => { void handleReopenDraft(); }}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-amber-600 bg-white"
                    >
                      <RotateCcw className="w-3 h-3" /> Corriger le brouillon
                    </button>
                  )}
                </div>
                <input
                  ref={replaceInputRef}
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    if (f) void handleReplacePdf(f);
                  }}
                />
              </div>
            )}
            {(refuseMode || visibleAnnotations.length > 0) && (
              <RefuseMarksPanel
                annotations={visibleAnnotations}
                refuseMode={refuseMode}
                refuseTool={refuseTool}
                onTool={setRefuseTool}
                selectedId={selectedAnnotationId}
                onSelect={setSelectedAnnotationId}
                onChangeText={(id, text) => setDraftAnnotations(setAnnotationText(draftAnnotations, id, text))}
                showMarks={showMarks}
                onShowMarks={setShowMarks}
              />
            )}
            {canPlace && !refuseMode && circuit.status === CIRCUIT_DRAFT && (
              <p className="text-xs text-cime-text-muted bg-neutral-50 border border-neutral-200 rounded-md px-3 py-2">
                {PARAPHEUR_COPY.dispatchHint}
              </p>
            )}
            {canPlace && !refuseMode && circuit.status !== CIRCUIT_DRAFT && (
              <p className="text-xs text-cime-text-muted bg-neutral-50 border border-neutral-200 rounded-md px-3 py-2">
                {PARAPHEUR_COPY.placeHint}{' '}
                {fillAndSignHint(waitingName, canStamp)}
              </p>
            )}
            {canStamp && !isValidation && zones.length === 0 && (
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                {PARAPHEUR_COPY.cannotSignNoBoxes}
              </p>
            )}
            {canStamp && zones.length > 0 && (
              <p className="text-xs text-cime-text-muted">
                {filledIds.length}/{zones.length} case{zones.length > 1 ? 's' : ''} remplie{zones.length > 1 ? 's' : ''}.
                Cliquez une case pour l’ôter.
              </p>
            )}
            <div className="text-xs text-cime-text-muted space-y-1">
              <div>
                Initié par{' '}
                {circuit.initiator?.id ? (
                  <CollaboratorLink
                    collaboratorId={circuit.initiator.id}
                    title={`Voir le profil de ${circuit.initiator.full_name}`}
                  >
                    <span className="font-medium text-neutral-700">{circuit.initiator.full_name}</span>
                  </CollaboratorLink>
                ) : (
                  <span className="font-medium text-neutral-700">{circuit.initiator?.full_name ?? '—'}</span>
                )}
              </div>
              <div>
                Fichier : <span className="font-medium text-neutral-700">{circuit.file?.filename ?? '—'}</span>
              </div>
              {circuit.dossier && (
                <div>
                  Dossier :{' '}
                  <Link to={`/dossiers/${circuit.dossier.id}`} className="font-medium text-cime-primary hover:underline">
                    {circuit.dossier.title}
                  </Link>
                </div>
              )}
            </div>

            <CircuitTimeline circuit={circuit} />

            {error && (
              <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
          </div>

          <footer className="flex items-center justify-between gap-2 px-4 py-3 border-t border-neutral-200">
            <div>
              {canCancel && (
                <button
                  type="button"
                  onClick={() => setCancelDialogOpen(true)}
                  disabled={cancel.isPending}
                  className="text-sm px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-md disabled:opacity-40"
                >
                  Retirer
                </button>
              )}
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              {circuit.status === CIRCUIT_DRAFT && (
                <button
                  type="button"
                  onClick={handleDispatch}
                  disabled={dispatch.isPending || !canDispatch}
                  title={canDispatch ? undefined : PARAPHEUR_COPY.needBoxesToSend}
                  className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-cime-primary text-white hover:opacity-90 disabled:opacity-40"
                >
                  <Send className="w-3.5 h-3.5" /> {PARAPHEUR_COPY.ctaSend}
                </button>
              )}
              {canRevise && (
                <button
                  type="button"
                  onClick={handleResubmit}
                  disabled={resubmit.isPending || !canResend}
                  title={
                    canResend
                      ? undefined
                      : isValidation
                        ? 'Remplacez le PDF avant de renvoyer'
                        : 'Remplacez le PDF et posez au moins une case avant de renvoyer'
                  }
                  className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-cime-primary text-white hover:opacity-90 disabled:opacity-40"
                >
                  Renvoyer
                </button>
              )}
              {canStamp && refuseMode && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setRefuseMode(false);
                      setDraftAnnotations([]);
                    }}
                    className="text-sm px-3 py-1.5 text-cime-text-muted hover:text-neutral-900"
                  >
                    Retour
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowReject(true)}
                    disabled={!hasRefuseMarks(draftAnnotations)}
                    className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-red-600 text-white hover:opacity-90 disabled:opacity-40"
                  >
                    <Ban className="w-3.5 h-3.5" /> Renvoyer
                  </button>
                </>
              )}
              {canStamp && !refuseMode && (
                <>
                  {!isValidation && zones.length > 0 && (
                    <button
                      type="button"
                      onClick={handleParaferTout}
                      disabled={saveStamps.isPending || boxesReady}
                      className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-neutral-300 text-neutral-700 hover:bg-neutral-50 disabled:opacity-40"
                    >
                      <Stamp className="w-3.5 h-3.5" /> {PARAPHEUR_COPY.fillAll}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setDraftAnnotations([]);
                      setSelectedAnnotationId(null);
                      setRefuseMode(true);
                    }}
                    disabled={reject.isPending}
                    className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-40"
                  >
                    <Ban className="w-3.5 h-3.5" /> Renvoyer
                  </button>
                  <button
                    type="button"
                    onClick={handleSign}
                    disabled={sign.isPending || !boxesReady}
                    className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-cime-primary text-white hover:opacity-90 disabled:opacity-40"
                  >
                    <Check className="w-3.5 h-3.5" /> {isValidation ? PARAPHEUR_COPY.ctaValidate : PARAPHEUR_COPY.ctaSign}
                  </button>
                </>
              )}
            </div>
          </footer>
        </aside>
      </div>

      <Dialog
        open={Boolean(highlightDialogId)}
        onOpenChange={(open) => {
          if (!open) setHighlightDialogId(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Remarque sur le surlignage</DialogTitle>
            <DialogDescription>
              Explique ce qui ne va pas sur ce passage. Tu pourras encore déplacer le surlignage ensuite.
            </DialogDescription>
          </DialogHeader>
          <textarea
            value={highlightDraftText}
            onChange={(e) => setHighlightDraftText(e.target.value)}
            rows={4}
            placeholder="Pourquoi ce passage pose problème…"
            className="w-full border border-neutral-200 rounded-md px-3 py-2 text-sm text-neutral-800"
          />
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setHighlightDialogId(null)}
            >
              Plus tard
            </Button>
            <Button type="button" size="sm" onClick={confirmHighlightText}>
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Retirer la demande</DialogTitle>
            <DialogDescription>
              « {circuitTitle} » ne sera plus en visa. Le PDF reste dans le dossier.
              Tu pourras redemander un visa dessus.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setCancelDialogOpen(false)}
              disabled={cancel.isPending}
            >
              Retour
            </Button>
            <Button
              type="button"
              variant="danger"
              size="sm"
              isLoading={cancel.isPending}
              onClick={handleConfirmCancel}
            >
              Retirer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {showVisaAuth && (
        <VisaReauthDialog
          onConfirm={handleConfirmVisa}
          onClose={() => setShowVisaAuth(false)}
          pending={sign.isPending}
          error={error}
          title={isValidation ? PARAPHEUR_COPY.confirmValidation : PARAPHEUR_COPY.confirmVisa}
          hint={isValidation ? PARAPHEUR_COPY.reauthHintValidation : PARAPHEUR_COPY.reauthHint}
          cta={isValidation ? PARAPHEUR_COPY.ctaValidate : PARAPHEUR_COPY.ctaSign}
        />
      )}
      {showReject && (
        <RejectCommentDialog
          onConfirm={handleReject}
          onClose={() => setShowReject(false)}
          pending={reject.isPending}
        />
      )}
      {resumeDraft && circuit.dossier?.id && (
        <GenerateDocumentModal
          dossierId={circuit.dossier.id}
          existingDraft={resumeDraft}
          replaceFileId={circuit.file?.id ?? null}
          onRevisionSaved={() => {
            setResumeDraft(null);
            persistZones([]);
            void qc.invalidateQueries({ queryKey: signatureKeys.all });
            void qc.invalidateQueries({ queryKey: ['dossier-files'] });
          }}
          onClose={() => setResumeDraft(null)}
        />
      )}
    </div>
  );
}
